// eslint-disable-next-line eag/match-named-export
import {z} from 'zod';

// ============================================================================
// HEALTH ENDPOINTS
// ============================================================================

export const HealthResponseSchema = z.object({
  /** Service status */
  status: z.string(),
  /** Total number of models loaded */
  models_loaded: z.number(),
  /** Number of regression models */
  regression_models: z.number(),
  /** Number of classification models */
  classification_models: z.number(),
  /** API version */
  version: z.string(),
  /** Model version */
  model_version: z.string(),
});

export type HealthResponse = z.infer<typeof HealthResponseSchema>;

export const ModelsInfoResponseSchema = z.object({
  /** Available repair categories */
  categories: z.array(z.string()),
  /** Available time windows */
  windows: z.array(z.record(z.any())),
  /** Total number of models */
  total_models: z.number(),
});

export type ModelsInfoResponse = z.infer<typeof ModelsInfoResponseSchema>;

// ============================================================================
// CATEGORY TYPES
// ============================================================================

export const CLASSIFIED_CATEGORY_IDS = [
  'AIR_CONDITIONING',
  'BODY',
  'BRAKE_SYSTEM',
  'CLUTCH',
  'DIFFERENTIAL',
  'DOORS_WINDOWS',
  'ELECTRICAL',
  'ENGINE_ACCESSORIES',
  'ENGINE_COOLING',
  'ENGINE_ELECTRICAL',
  'ENGINE_INTAKE',
  'ENGINE_LUBRICATION',
  'ENGINE_MECHANICAL',
  'EXHAUST_SYSTEM',
  'FUEL_SYSTEM',
  'HEATING',
  'IGNITION',
  'INSTRUMENTS',
  'LIGHTING_EXTERIOR',
  'LIGHTING_INTERIOR',
  'LINKAGE_SYSTEM',
  'SAFETY_AND_ASSISTANCE_SYSTEMS',
  'STEERING',
  'TIMING_MECHANISM',
  'TRANSMISSION_AUTOMATIC',
  'TRANSMISSION_MANUAL',
  'WHEELS_TIRES',
  'WHEEL_SUSPENSION',
  'WIPERS_WASHERS',
] as const;

export const RUL_ONLY_CATEGORY_IDS = ['COMFORT_FEATURES', 'INFOTAINMENT'] as const;

export const RUL_CATEGORY_IDS = [...CLASSIFIED_CATEGORY_IDS, ...RUL_ONLY_CATEGORY_IDS] as const;

export const CATEGORY_IDS = RUL_CATEGORY_IDS;

export const CategoryIdSchema = z.enum(CATEGORY_IDS);

export type CategoryId = z.infer<typeof CategoryIdSchema>;

export const CategoryInfoSchema = z.object({
  /** Category identifier */
  id: CategoryIdSchema,
  /** Display name */
  name: z.string(),
  /** Description */
  description: z.string(),
});

export type CategoryInfo = z.infer<typeof CategoryInfoSchema>;

export const CategoriesResponseSchema = z.object({
  /** List of all repair categories */
  categories: z.array(CategoryInfoSchema),
  /** Total number of categories */
  total: z.number(),
});

export type CategoriesResponse = z.infer<typeof CategoriesResponseSchema>;

// ============================================================================
// PREDICTION TYPES
// ============================================================================

export const CategoryPredictionSchema = z.object({
  /** Will this repair be needed? */
  will_need: z.boolean(),
  /** Confidence score (0.0-1.0) */
  confidence: z.number(),
});

export type CategoryPrediction = z.infer<typeof CategoryPredictionSchema>;

export const PredictionResponseSchema = z.object({
  /** Vehicle Identification Number */
  vin: z.string(),
  /** Predictions organized by time window and category */
  predictions: z.record(z.record(CategoryPredictionSchema)),
  /** Request ID */
  request_id: z.string(),
});

export type PredictionResponse = z.infer<typeof PredictionResponseSchema>;

/** VIN-based prediction request (fetches vehicle data from database) */
export const VinPredictionRequestSchema = z.object({
  /** Vehicle Identification Number */
  vin: z.string(),
  /** Time windows to predict (e.g., ['3_1', '5_2']). If not specified, all windows will be used. */
  windows: z.array(z.string()).nullable().optional(),
  /** Override car_style (e.g. `CARSTYLE_HATCHBACK`) — primary v2 categorical feature */
  car_style: z
    .string()
    .regex(/^CARSTYLE_/)
    .optional(),
  /** Override vehicle line */
  vehicle_line: z.string().optional(),
  /** Override body type */
  body_type: z.string().optional(),
  /** Override engine capacity */
  engine_capacity: z.union([z.string(), z.number()]).optional(),
  /** Override engine type */
  engine_type: z.string().optional(),
  /** Override fuel type */
  fuel_type: z.string().optional(),
  /** Override transaxle */
  transaxle: z.string().optional(),
});

export type VinPredictionRequest = z.infer<typeof VinPredictionRequestSchema>;

// ============================================================================
// VIN AVAILABILITY CHECK
// ============================================================================

export const VinCheckResponseSchema = z.object({
  vin: z.string(),
  exists: z.boolean(),
});

export type VinCheckResponse = z.infer<typeof VinCheckResponseSchema>;

// ============================================================================
// STATELESS PREDICTION (OMN-11)
// ============================================================================

export const VehicleDataSchema = z.object({
  make: z.string().nullable().optional(),
  model_family: z.string().nullable().optional(),
  /**
   * V2 categorical input — primary feature consumed by the v2 model.
   * Always prefixed with `CARSTYLE_` (e.g. `CARSTYLE_HATCHBACK`). The backend
   * warn-validates the prefix and rejects nothing.
   */
  car_style: z
    .string()
    .regex(/^CARSTYLE_/)
    .nullable()
    .optional(),
  /** Legacy DMS-derived body style (e.g. `BODY_HATCHBACK`). Kept for v0/v1 fallback. */
  body_style: z.string().nullable().optional(),
  engine_volume: z.number().nullable().optional(),
  transmission: z.string().nullable().optional(),
  fuel_type: z.string().nullable().optional(),
  model_year: z.number().nullable().optional(),
  first_registration: z.string().nullable().optional(),
  mileage: z.number().nullable().optional(),
});

export type VehicleData = z.infer<typeof VehicleDataSchema>;

export const StatelessPredictionRequestSchema = z.object({
  vin: z.string().nullable().optional(),
  vehicle_data: VehicleDataSchema,
  repair_history: z.array(z.record(z.unknown())).optional(),
  windows: z.array(z.string()).nullable().optional(),
});

export type StatelessPredictionRequest = z.infer<typeof StatelessPredictionRequestSchema>;

export const StatelessPredictionResponseSchema = PredictionResponseSchema;

export type StatelessPredictionResponse = z.infer<typeof StatelessPredictionResponseSchema>;

// ============================================================================
// VALIDATION ERROR TYPES
// ============================================================================

export const ValidationErrorSchema = z.object({
  loc: z.array(z.union([z.string(), z.number()])),
  msg: z.string(),
  type: z.string(),
});

export type ValidationError = z.infer<typeof ValidationErrorSchema>;

export const HttpValidationErrorSchema = z.object({
  detail: z.array(ValidationErrorSchema).optional(),
});

export type HttpValidationError = z.infer<typeof HttpValidationErrorSchema>;

// ============================================================================
// API ARG/RESPONSE TYPES
// ============================================================================

export type HealthCheckApiV1HealthGetApiResponse = HealthResponse;
export type HealthCheckApiV1HealthGetApiArg = void;
export type GetModelsInfoApiV1HealthModelsGetApiResponse = ModelsInfoResponse;
export type GetModelsInfoApiV1HealthModelsGetApiArg = void;

// VIN-based prediction
export type PredictByVinApiResponse = PredictionResponse;
export type PredictByVinApiArg = {
  vinPredictionRequest: VinPredictionRequest;
};

// Categories
export type GetCategoriesApiResponse = CategoriesResponse;
export type GetCategoriesApiArg = void;

// VIN availability check
export type CheckVinAvailabilityApiArg = {vin: string};
export type CheckVinAvailabilityApiResponse = VinCheckResponse;

// Stateless prediction
export type PredictStatelessApiArg = {statelessPredictionRequest: StatelessPredictionRequest};
export type PredictStatelessApiResponse = StatelessPredictionResponse;

export const DataCompletenessSchema = z.enum(['full', 'partial']);

export type DataCompleteness = z.infer<typeof DataCompletenessSchema>;

export const RulCategoryPredictionSchema = z.object({
  rul_days: z.number().nullable(),
});

export type RulCategoryPrediction = z.infer<typeof RulCategoryPredictionSchema>;

export const RulPredictionResponseSchema = z.object({
  request_id: z.string(),
  vin: z.string().nullable().optional(),
  data_completeness: DataCompletenessSchema,
  rul_predictions: z.record(RulCategoryPredictionSchema),
});

export type RulPredictionResponse = z.infer<typeof RulPredictionResponseSchema>;

export const VinRulRequestSchema = z.object({
  vin: z.string(),
  mileage: z.number().optional(),
  car_style: z
    .string()
    .regex(/^CARSTYLE_/)
    .optional(),
  vehicle_line: z.string().optional(),
  body_type: z.string().optional(),
  engine_capacity: z.union([z.string(), z.number()]).optional(),
  engine_type: z.string().optional(),
  fuel_type: z.string().optional(),
  transaxle: z.string().optional(),
});

export type VinRulRequest = z.infer<typeof VinRulRequestSchema>;

export const StatelessRulRequestSchema = z.object({
  vin: z.string().nullable().optional(),
  vehicle_data: VehicleDataSchema,
  repair_history: z.array(z.record(z.unknown())).optional(),
});

export type StatelessRulRequest = z.infer<typeof StatelessRulRequestSchema>;

export type PredictByVinRulApiArg = {vinRulRequest: VinRulRequest};
export type PredictByVinRulApiResponse = RulPredictionResponse;

export type PredictStatelessRulApiArg = {statelessRulRequest: StatelessRulRequest};
export type PredictStatelessRulApiResponse = RulPredictionResponse;
